import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, InputGroup, FormControl } from 'react-bootstrap';
import Spinner from './Spinner';
import { toast } from 'react-toastify';
import IconPicker from './IconPicker';
import { api, API_BASE_URL } from '../configs/apiConfig';
import desktopService from '../services/desktopService';
import PageService from '../services/PageService';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { BsPlusLg, BsPencil, BsTrash, BsGrid3X3Gap, BsGridFill, BsChevronUp, BsChevronDown } from 'react-icons/bs';
import * as FaIcons from 'react-icons/fa';
import { FaTh, FaSearch, FaUser, FaBuilding, FaPlus, FaMinus, FaTrash, FaTimes, FaFile } from 'react-icons/fa';
import FileService from '../services/FileService';
import DesktopAccessControlService from '../services/DesktopAccessControlService';
import OrganizationalUnitService from '../services/OrganizationalUnitService';
import ProfileImage from '../components/ProfileImage';
import { motion, AnimatePresence } from 'framer-motion';
import { getIconComponent } from './IconPicker';
import '../assets/styles/DesktopManagement.css';

// DesktopManagement component styles are handled in the CSS file

// Internal Modal component for access control
const DesktopAccessControlModal = ({ isOpen, title, onClose, children, footer }) => {
    if (!isOpen) return null;
  
    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="access-modal-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="access-modal-content"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="access-modal-header">
                            <h3 className="access-modal-title">{title}</h3>
                            <button className="access-modal-close" onClick={onClose}>
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="access-modal-body">
                            {children}
                        </div>
                        
                        {footer && (
                            <div className="access-modal-footer">
                                {footer}
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const DesktopManagement = () => {
    const [desktops, setDesktops] = useState([]);
    const [panels, setPanels] = useState([]);
    const [showDesktopModal, setShowDesktopModal] = useState(false);
    const [showPanelModal, setShowPanelModal] = useState(false);
    const [showPanelEditModal, setShowPanelEditModal] = useState(false);
    const [selectedDesktop, setSelectedDesktop] = useState(null);
    const [selectedPanel, setSelectedPanel] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        iconCode: '',
        position: 'left',
        order: 0,
        size: 'medium',
        code: '',
        desktopId: null,
        iconImage: '',
        desktopType: '',
        url: '',
        pageCode: '',
        customType: ''
    });
    const [panelFormData, setPanelFormData] = useState({
        name: '',
        description: '',
        iconCode: '',
        iconImage: '',
        code: '',
        position: 'left',
        order: 0,
        size: 'medium',
        desktopId: null
    });
    const [loading, setLoading] = useState(true);
    const [showLandingItemsModal, setShowLandingItemsModal] = useState(false);
    const [showLandingItemEditModal, setShowLandingItemEditModal] = useState(false);
    const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
    const [showAddItemModal, setShowAddItemModal] = useState(false);
    const [landingItems, setLandingItems] = useState([]);
    const [expandedCategories, setExpandedCategories] = useState(new Set());
    const [selectedLandingItem, setSelectedLandingItem] = useState(null);
    const [selectedTreeNode, setSelectedTreeNode] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [landingItemFormData, setLandingItemFormData] = useState({
        name: '',
        description: '',
        iconCode: '',
        iconImage: '',
        pageCode: '',
        url: '',
        linkType: 'Internal',
        order: 0,
        selectedFile: null,
        parentId: null,
        isParent: false
    });
    const [pages, setPages] = useState([]);
    const [loadingPages, setLoadingPages] = useState(false);

    // Access Control State
    const [orgUnits, setOrgUnits] = useState([]);
    const [expandedUnits, setExpandedUnits] = useState({});
    const [unitSearch, setUnitSearch] = useState('');
    const [desktopAccessInfo, setDesktopAccessInfo] = useState({});
    const [showAccessModal, setShowAccessModal] = useState(false);
    const [accessModalData, setAccessModalData] = useState({
        type: '', // 'user' or 'unit'
        sourceId: null,
        sourceName: '',
        targetId: null,
        targetName: '',
        accessTypes: [],
        selectedAccessType: null,
        exclusion: false,
        exclusions: [],
        childrenToExclude: []
    });
    const [exclusionSearch, setExclusionSearch] = useState('');
    const [exclusionSuggestions, setExclusionSuggestions] = useState([]);
    const [showExclusionSuggestions, setShowExclusionSuggestions] = useState(false);
    const [isSmallPanel, setIsSmallPanel] = useState(false);
    const [panelWidth, setPanelWidth] = useState(0);
    const [theme, setTheme] = useState('light');

    const codeOptions = ['Main', 'Modules', 'Tasks'];

    const [disabledCodes, setDisabledCodes] = useState({});

    const isCodeOptionDisabled = async (option) => {
        try {
            const isValid = await desktopService.validatePanelCode(
                selectedDesktop.id,
                option,
                selectedPanel?.id
            );
            return !isValid;
        } catch (error) {
            console.error('Error validating code:', error);
            return true;
        }
    };

    // Access Control Functions - Define before useEffect
    const fetchOrganizationalUnits = async () => {
        try {
            const unitsData = await OrganizationalUnitService.getOrganizationChart();
            if (unitsData && unitsData.length > 0) {
                setOrgUnits(unitsData);
                
                // Initialize expanded states
                const unitExpanded = {};
                const initializeExpandedState = (units) => {
                    units.forEach(unit => {
                        unitExpanded[unit.id] = false;
                        if (unit.children && unit.children.length > 0) {
                            initializeExpandedState(unit.children);
                        }
                    });
                };
                initializeExpandedState(unitsData);
                setExpandedUnits(unitExpanded);
            }
        } catch (err) {
            toast.error('Failed to load organizational units');
        }
    };

    const fetchDesktopAccessInfo = async (desktopId) => {
        try {
            const accessInfo = await DesktopAccessControlService.getAccessControlsByDesktop(desktopId);
            
            // The response now comes in the format: { desktopId, userAccesses: [], unitAccesses: [] }
            setDesktopAccessInfo(prev => ({
                ...prev,
                [desktopId]: {
                    userAccesses: accessInfo.userAccesses || [],
                    unitAccesses: accessInfo.unitAccesses || []
                }
            }));
        } catch (err) {
            console.error('Failed to fetch desktop access info:', err);
        }
    };

    const toggleUnitExpand = (unitId) => {
        setExpandedUnits(prev => ({
            ...prev,
            [unitId]: !prev[unitId]
        }));
    };

    const filterUnits = (units, searchTerm) => {
        if (!searchTerm) return units;
        
        return units.filter(unit => {
            const matchesName = unit.name.toLowerCase().includes(searchTerm.toLowerCase());
            const hasMatchingChildren = unit.children && filterUnits(unit.children, searchTerm).length > 0;
            const hasMatchingUsers = unit.assignedUsers && unit.assignedUsers.some(user => 
                user.userFullName.toLowerCase().includes(searchTerm.toLowerCase())
            );
            
            return matchesName || hasMatchingChildren || hasMatchingUsers;
        });
    };

    const handleDragEnd = async (result) => {
        const { source, destination, draggableId } = result;
        
        if (!destination) {
            return;
        }
        
        const draggedItemType = draggableId.split('-')[0];
        const draggedItemId = draggableId.split('-')[1];
        
        if (destination.droppableId.startsWith('desktop-')) {
            const desktopId = destination.droppableId.replace('desktop-', '');
            
            const findDesktopName = (desktops, id) => {
                const desktop = desktops.find(d => d.id === parseInt(id));
                return desktop ? desktop.name : `Desktop ${id}`;
            };
            
            const desktopName = findDesktopName(desktops, desktopId);
            
            if (draggedItemType === 'unit') {
                const findUnitName = (units, id) => {
                    const idStr = String(id);
                    const idNum = Number(id);
                    
                    for (const unit of units) {
                        if (String(unit.id) === idStr || Number(unit.id) === idNum) return unit.name;
                        if (unit.children && unit.children.length > 0) {
                            const childResult = findUnitName(unit.children, id);
                            if (childResult) return childResult;
                        }
                    }
                    return null;
                };
                
                const unitName = findUnitName(orgUnits, draggedItemId);
                
                setAccessModalData({
                    type: 'unit',
                    sourceId: draggedItemId,
                    sourceName: unitName || `Unit ${draggedItemId}`,
                    targetId: desktopId,
                    targetName: desktopName || `Desktop ${desktopId}`,
                    accessTypes: [],
                    selectedAccessType: null,
                    exclusion: false,
                    exclusions: [],
                    childrenToExclude: []
                });
                setShowAccessModal(true);
                setExclusionSearch('');
                
                fetchUnitChildren(draggedItemId);
            } else if (draggedItemType === 'user') {
                const findUserName = (units, userId) => {
                    const userIdStr = String(userId);
                    const userIdNum = Number(userId);
                    
                    for (const unit of units) {
                        const unitUsers = unit.assignedUsers || unit.users || [];
                        
                        const user = unitUsers.find(u => 
                            String(u.userId) === userIdStr || 
                            Number(u.userId) === userIdNum ||
                            String(u.id) === userIdStr || 
                            Number(u.id) === userIdNum
                        );
                        
                        if (user) {
                            return user.userFullName || user.fullName || user.name || 'Unknown User';
                        }
                        
                        if (unit.children && unit.children.length > 0) {
                            const name = findUserName(unit.children, userId);
                            if (name) return name;
                        }
                    }
                    return 'Unknown User';
                };
                
                const userName = findUserName(orgUnits, draggedItemId);
                
                setAccessModalData({
                    type: 'user',
                    sourceId: draggedItemId,
                    sourceName: userName || `User ${draggedItemId}`,
                    targetId: desktopId,
                    targetName: desktopName || `Desktop ${desktopId}`,
                    accessTypes: [],
                    selectedAccessType: null,
                    exclusion: false,
                    exclusions: [],
                    childrenToExclude: []
                });
                setShowAccessModal(true);
                setExclusionSearch('');
            }
        }
    };

    const handleAssignAccess = async () => {
        try {
            // Check if access already exists
            const desktopAccess = desktopAccessInfo[accessModalData.targetId];
            if (desktopAccess) {
                if (accessModalData.type === 'unit') {
                    const unitExists = desktopAccess.unitAccesses?.some(unit => unit.unitId === parseInt(accessModalData.sourceId));
                    if (unitExists) {
                        toast.error('This unit already has access to this desktop');
                        return;
                    }
                } else {
                    const userExists = desktopAccess.userAccesses?.some(user => user.userId === parseInt(accessModalData.sourceId));
                    if (userExists) {
                        toast.error('This user already has access to this desktop');
                        return;
                    }
                }
            }

            const accessData = {
                desktopId: parseInt(accessModalData.targetId),
                userId: accessModalData.type === 'user' ? parseInt(accessModalData.sourceId) : null,
                organizationalUnitId: accessModalData.type === 'unit' ? parseInt(accessModalData.sourceId) : null
            };

            await DesktopAccessControlService.createAccessControl(accessData);

            // Process exclusions if any
            if (accessModalData.exclusions && accessModalData.exclusions.length > 0) {
                for (const exclusion of accessModalData.exclusions) {
                    const exclusionData = {
                        desktopId: parseInt(accessModalData.targetId),
                        isExcluded: true,
                        userId: exclusion.type === 'user' ? parseInt(exclusion.id) : null,
                        organizationalUnitId: exclusion.type === 'unit' ? parseInt(exclusion.id) : null
                    };

                    await DesktopAccessControlService.createAccessControl(exclusionData);
                }
            }

            // Refresh desktop access info
            await fetchDesktopAccessInfo(accessModalData.targetId);

            setShowAccessModal(false);
            toast.success('Access assigned successfully');
        } catch (err) {
            toast.error('Failed to assign access. Please try again.');
        }
    };

    const fetchUnitChildren = async (unitId) => {
        try {
            const unitResponse = await OrganizationalUnitService.getUnitById(unitId);
            const childUnits = unitResponse.children || [];
            
            const users = await OrganizationalUnitService.getUsersByUnitId(unitId);
            
            const formattedUnits = childUnits.map(unit => ({
                id: unit.id,
                name: unit.name,
                type: 'unit'
            }));
            
            const formattedUsers = users.map(user => ({
                id: user.id,
                name: user.firstName && user.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : user.username || 'Unknown User',
                type: 'user'
            }));
            
            const children = [...formattedUnits, ...formattedUsers];
            
            setAccessModalData(prev => ({
                ...prev,
                childrenToExclude: children
            }));
            
            return children;
        } catch (err) {
            toast.error('Failed to fetch unit children');
            return [];
        }
    };

    const handleExclusionSearch = (searchTerm) => {
        setExclusionSearch(searchTerm);
        
        if (!searchTerm) {
            setExclusionSuggestions([]);
            setShowExclusionSuggestions(false);
            return;
        }
        
        const filteredSuggestions = accessModalData.childrenToExclude.filter(item => 
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
            !accessModalData.exclusions.some(exclusion => exclusion.id === item.id && exclusion.type === item.type)
        );
        
        setExclusionSuggestions(filteredSuggestions);
        setShowExclusionSuggestions(true);
    };

    const addExclusion = (item) => {
        setAccessModalData(prev => ({
            ...prev,
            exclusions: [...prev.exclusions, item]
        }));
        setExclusionSearch('');
        setShowExclusionSuggestions(false);
    };

    const removeExclusion = (id, type) => {
        setAccessModalData(prev => ({
            ...prev,
            exclusions: prev.exclusions.filter(item => !(item.id === id && item.type === type))
        }));
    };

    const handleRemoveAccess = async (accessId, desktopId) => {
        try {
            await DesktopAccessControlService.deleteAccessControl(accessId);
            
            // Refresh desktop access info for the specific desktop
            await fetchDesktopAccessInfo(desktopId);
            
            toast.success('Access removed successfully');
        } catch (err) {
            toast.error('Failed to remove access. Please try again.');
        }
    };

    const handleAccessModalClose = () => {
        setShowAccessModal(false);
        setExclusionSearch('');
        setExclusionSuggestions([]);
        setShowExclusionSuggestions(false);
        setAccessModalData(prev => ({
            ...prev,
            selectedAccessType: null,
            exclusion: false,
            exclusions: []
        }));
    };

    // Render organizational unit tree
    const renderUnitTree = (units, level = 0) => {
        if (!units || units.length === 0) return null;
        
        const filteredUnits = filterUnits(units, unitSearch);
        
        if (filteredUnits.length === 0) return null;
        
        return (
            <ul className="unit-tree" style={{ paddingLeft: level > 0 ? '20px' : '0' }}>
                {filteredUnits.map((unit, index) => {
                    const unitUsers = unit.assignedUsers || unit.users || [];
                    
                    return (
                        <li key={`unit-${unit.id}`} className="unit-item">
                            <div className="unit-header">
                                <button 
                                    className="toggle-btn"
                                    onClick={() => toggleUnitExpand(unit.id)}
                                >
                                    {expandedUnits[unit.id] ? <FaMinus /> : <FaPlus />}
                                </button>
                                <Draggable draggableId={`unit-${unit.id}`} index={index} key={`unit-${unit.id}`}>
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className="unit-label"
                                            style={{
                                                ...provided.draggableProps.style,
                                                backgroundColor: unit.unitTypeColor || '#3498db',
                                                color: 'white',
                                                fontWeight: '500'
                                            }}
                                        >
                                            <span style={{ marginRight: '5px' }}>
                                                {getIconComponent(unit.unitTypeIcon) || <FaBuilding />}
                                            </span>
                                            {unit.name}
                                        </div>
                                    )}
                                </Draggable>
                            </div>
                            
                            {expandedUnits[unit.id] && (
                                <>
                                    {unitUsers && unitUsers.length > 0 ? (
                                        <ul className="user-list">
                                            {unitUsers.map((user, userIndex) => (
                                                <div key={`user-${user.userId || user.id}`}>
                                                    <Draggable draggableId={`user-${user.userId || user.id}`} index={userIndex}>
                                                        {(provided) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                className="user-label"
                                                            >
                                                                <FaUser style={{ marginRight: '5px' }} />
                                                                {user.userFullName || user.fullName || user.name || 'Unknown User'}
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                </div>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="no-users-message">No users assigned to this unit</div>
                                    )}
                                    
                                    {unit.children && unit.children.length > 0 && renderUnitTree(unit.children, level + 1)}
                                </>
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    };

    useEffect(() => {
        fetchDesktops();
        fetchPages();
        fetchOrganizationalUnits();
    }, []);

    // Detect panel size and adjust layout
    const checkPanelSize = useCallback(() => {
        const container = document.querySelector('.desktop-management-panel');
        if (container) {
            const width = container.offsetWidth;
            setPanelWidth(width);
            const newIsSmallPanel = width < 768;
            setIsSmallPanel(newIsSmallPanel);
            console.log('Panel size detected:', width, 'isSmallPanel:', newIsSmallPanel);
        }
    }, []);

    useEffect(() => {
        // Initial check with a small delay to ensure DOM is ready
        const initialCheck = () => {
            setTimeout(checkPanelSize, 100);
        };
        initialCheck();
        
        // Set up multiple event listeners to catch different types of size changes
        window.addEventListener('resize', checkPanelSize);
        
        // Use ResizeObserver for more reliable size change detection
        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target.classList.contains('desktop-management-panel')) {
                    checkPanelSize();
                }
            }
        });

        const container = document.querySelector('.desktop-management-panel');
        if (container) {
            resizeObserver.observe(container);
        }

        // Also check periodically to catch any missed changes
        const intervalId = setInterval(checkPanelSize, 100);
        
        // Add mutation observer to catch DOM changes that might affect layout
        const mutationObserver = new MutationObserver(() => {
            checkPanelSize();
        });

        if (container) {
            mutationObserver.observe(container, {
                attributes: true,
                childList: true,
                subtree: true
            });
        }

        // Also observe the parent container for size changes
        const parentContainer = container?.parentElement;
        let parentResizeObserver = null;
        if (parentContainer) {
            parentResizeObserver = new ResizeObserver(() => {
                checkPanelSize();
            });
            parentResizeObserver.observe(parentContainer);
        }
        
        return () => {
            window.removeEventListener('resize', checkPanelSize);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            if (parentResizeObserver) {
                parentResizeObserver.disconnect();
            }
            clearInterval(intervalId);
        };
    }, [checkPanelSize]);

    // Additional effect to ensure responsive updates when panel width changes
    useEffect(() => {
        if (panelWidth > 0) {
            const newIsSmallPanel = panelWidth < 768;
            setIsSmallPanel(newIsSmallPanel);
            console.log('Panel width changed to:', panelWidth, 'isSmallPanel:', newIsSmallPanel);
        }
    }, [panelWidth]);



    // Add a global function to force panel size check
    useEffect(() => {
        window.forcePanelSizeCheck = checkPanelSize;
        
        return () => {
            delete window.forcePanelSizeCheck;
        };
    }, [checkPanelSize]);

    // Add a more aggressive check for panel size changes
    useEffect(() => {
        const aggressiveCheck = () => {
            setTimeout(checkPanelSize, 50);
            setTimeout(checkPanelSize, 150);
            setTimeout(checkPanelSize, 300);
        };

        // Check more frequently when the component mounts
        aggressiveCheck();
        
        // Also check when the window gains focus (panel might have been maximized)
        const handleFocus = () => {
            setTimeout(checkPanelSize, 100);
        };
        
        window.addEventListener('focus', handleFocus);
        
        // Also check when the document becomes visible again
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                setTimeout(checkPanelSize, 100);
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [checkPanelSize]);

    // Theme detection effect
    useEffect(() => {
        const checkTheme = () => {
            const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';
            setTheme(currentTheme);
        };
        
        checkTheme();
        
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.attributeName === 'data-theme') {
                    checkTheme();
                }
            });
        });
        
        observer.observe(document.documentElement, { attributes: true });
        
        return () => observer.disconnect();
    }, []);

    // Force text color based on theme and environment
    const textStyle = {
        color: theme === 'dark' ? '#ffffff' : '#333333'
    };

    useEffect(() => {
        const updateDisabledCodes = async () => {
            if (selectedDesktop) {
                const newDisabledCodes = {};
                for (const option of codeOptions) {
                    const isValid = await desktopService.validatePanelCode(
                        selectedDesktop.id,
                        option,
                        selectedPanel?.id
                    );
                    newDisabledCodes[option] = !isValid;
                }
                setDisabledCodes(newDisabledCodes);
            }
        };
        updateDisabledCodes();
    }, [selectedDesktop, selectedPanel, panels]);

    const fetchDesktops = async () => {
        try {
            const response = await api.get('/Desktop');
            const sortedDesktops = response.data.sort((a, b) => a.order - b.order);
            setDesktops(sortedDesktops);
            
            // Fetch access info for each desktop
            for (const desktop of sortedDesktops) {
                await fetchDesktopAccessInfo(desktop.id);
            }
        } catch (error) {
            toast.error('Failed to fetch desktops');
        } finally {
            setLoading(false);
        }
    };

    const fetchPages = async () => {
        try {
            setLoadingPages(true);
            const pagesData = await PageService.getActive();
            setPages(pagesData);
        } catch (error) {
            toast.error('Failed to fetch pages');
            console.error('Error fetching pages:', error);
        } finally {
            setLoadingPages(false);
        }
    };

    const loadPanels = async (desktopId) => {
        try {
            const data = await desktopService.getDesktopPanels(desktopId);
            setPanels(data);
        } catch (error) {
            toast.error('Failed to load panels');
        }
    };

    const handleDesktopSelect = async (desktop) => {
        setSelectedDesktop(desktop);
        await loadPanels(desktop.id);
    };

    const handleDesktopSubmit = async (e) => {
        e.preventDefault();
        try {
            // Format URL for external links
            let formattedUrl = formData.url;
            if (formData.desktopType === 'External Link' && formData.url) {
                // Add https:// if no protocol is specified
                if (!formData.url.match(/^https?:\/\//i)) {
                    formattedUrl = `https://${formData.url}`;
                }
            }

            const desktopData = {
                name: formData.name,
                iconCode: formData.iconCode,
                pageCode: formData.pageCode,
                url: formattedUrl,
                order: formData.order,
                isHome: formData.isHome,
                desktopType: formData.desktopType
            };

            // Only include description if it's not empty
            if (formData.description && formData.description.trim()) {
                desktopData.description = formData.description.trim();
            }

            const iconFile = formData.iconImage instanceof File ? formData.iconImage : null;

            if (selectedDesktop) {
                await desktopService.updateDesktop(selectedDesktop.id, desktopData, iconFile);
                toast.success('Desktop updated successfully');
            } else {
                await desktopService.createDesktop(desktopData, iconFile);
                toast.success('Desktop created successfully');
            }
            
            setShowDesktopModal(false);
            fetchDesktops();
        } catch (error) {
            toast.error('Failed to save desktop');
        }
    };

    const handlePanelSubmit = async (e) => {
        e.preventDefault();
        try {
            // Validate code before submitting
            const isValid = await desktopService.validatePanelCode(
                selectedDesktop.id,
                panelFormData.code,
                selectedPanel?.id
            );

            if (!isValid) {
                toast.error('A panel with this code already exists in this desktop');
                return;
            }

            const panelData = {
                name: panelFormData.name,
                iconCode: panelFormData.iconCode,
                code: panelFormData.code,
                position: panelFormData.position,
                order: panelFormData.order,
                size: panelFormData.size,
                desktopId: selectedDesktop.id
            };

            // Only include description if it's not empty
            if (panelFormData.description && panelFormData.description.trim()) {
                panelData.description = panelFormData.description.trim();
            }

            const iconFile = panelFormData.iconImage instanceof File ? panelFormData.iconImage : null;

            if (selectedPanel) {
                await desktopService.updatePanel(selectedPanel.id, panelData, iconFile);
                toast.success('Panel updated successfully');
            } else {
                await desktopService.createPanel(panelData, iconFile);
                toast.success('Panel created successfully');
            }
            setShowPanelModal(false);
            loadPanels(selectedDesktop.id);
        } catch (error) {
            toast.error('Failed to save panel');
        }
    };

    const handleDeleteDesktop = async (id) => {
        if (window.confirm('Are you sure you want to delete this desktop?')) {
            try {
                await desktopService.deleteDesktop(id);
                toast.success('Desktop deleted successfully');
                if (selectedDesktop && selectedDesktop.id === id) {
                    setSelectedDesktop(null);
                    setPanels([]);
                }
                fetchDesktops();
            } catch (error) {
                toast.error('Failed to delete desktop');
            }
        }
    };

    const handleDeletePanel = async (id) => {
        if (window.confirm('Are you sure you want to delete this panel?')) {
            try {
                await desktopService.deletePanel(id);
                toast.success('Panel deleted successfully');
                loadPanels(selectedDesktop.id);
            } catch (error) {
                toast.error('Failed to delete panel');
            }
        }
    };

    const handleEditPanel = (panel) => {
        setSelectedPanel(panel);
        setPanelFormData({
            name: panel.name,
            description: panel.description,
            iconCode: panel.iconCode,
            iconImage: panel.iconImage,
            code: panel.code,
            position: panel.position,
            order: panel.order,
            size: panel.size,
            desktopId: panel.desktopId
        });
        setShowPanelEditModal(true);
    };

    const handleDesktopTypeChange = (type) => {
        setFormData(prev => ({
            ...prev,
            desktopType: type === 'Dashboard' ? 'Dashboard' : type,
            url: type === 'External Link' ? prev.url : '',
            pageCode: type === 'Internal Link' ? prev.pageCode : '',
            customType: type === 'Other' ? prev.customType : ''
        }));
    };

    const handleDesktopReorder = async (result) => {
        if (!result.destination) return;

        const items = Array.from(desktops);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Update order numbers
        const updatedItems = items.map((item, index) => ({
            ...item,
            order: index
        }));

        setDesktops(updatedItems);

        try {
            const orderUpdates = updatedItems.map(item => ({
                id: item.id,
                order: item.order
            }));

            await api.post('/Desktop/reorder', orderUpdates);
            toast.success('Desktop order updated successfully');
        } catch (error) {
            toast.error('Failed to update desktop order');
            // Revert to previous state on error
            fetchDesktops();
        }
    };

    const handleMoveDesktop = async (desktopId, direction) => {
        const currentIndex = desktops.findIndex(d => d.id === desktopId);
        if (currentIndex === -1) return;

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= desktops.length) return;

        const items = Array.from(desktops);
        const [movedItem] = items.splice(currentIndex, 1);
        items.splice(newIndex, 0, movedItem);

        // Update order numbers
        const updatedItems = items.map((item, index) => ({
            ...item,
            order: index
        }));

        setDesktops(updatedItems);

        try {
            const orderUpdates = updatedItems.map(item => ({
                id: item.id,
                order: item.order
            }));

            await api.post('/Desktop/reorder', orderUpdates);
            toast.success('Desktop order updated successfully');
        } catch (error) {
            toast.error('Failed to update desktop order');
            // Revert to previous state on error
            fetchDesktops();
        }
    };

    const handleEdit = (desktop) => {
        setSelectedDesktop(desktop);
        setFormData({
            name: desktop.name,
            description: desktop.description,
            iconCode: desktop.iconCode,
            iconImage: desktop.iconImage,
            pageCode: desktop.pageCode,
            url: desktop.url,
            order: desktop.order,
            isHome: desktop.isHome,
            desktopType: desktop.desktopType
        });
        setShowDesktopModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this desktop?')) {
            try {
                await api.delete(`/Desktop/${id}`);
                toast.success('Desktop deleted successfully');
                fetchDesktops();
            } catch (error) {
                toast.error('Failed to delete desktop');
            }
        }
    };

    const handleManagePanels = async (desktop) => {
        setSelectedDesktop(desktop);
        await loadPanels(desktop.id);
        setShowPanelModal(true);
    };

    const handleSavePanel = async () => {
        try {
            if (!panelFormData.code) {
                toast.error('Please select a type');
                return;
            }

            const panelData = {
                name: panelFormData.name,
                iconCode: panelFormData.iconCode,
                code: panelFormData.code,
                position: panelFormData.position,
                order: panelFormData.order,
                size: panelFormData.size,
                desktopId: selectedDesktop.id
            };

            // Only include description if it's not empty
            if (panelFormData.description && panelFormData.description.trim()) {
                panelData.description = panelFormData.description.trim();
            }

            if (selectedPanel) {
                await desktopService.updatePanel(selectedPanel.id, panelData, panelFormData.iconImage instanceof File ? panelFormData.iconImage : null);
                toast.success('Panel updated successfully');
            } else {
                await desktopService.createPanel(panelData);
                toast.success('Panel created successfully');
            }
            setShowPanelEditModal(false);
            loadPanels(selectedDesktop.id);
        } catch (error) {
            toast.error('Failed to save panel');
        }
    };

    const renderIcon = (desktop) => {
        if (desktop.iconImage) {
            return (
                <div style={{ display: 'inline-block', marginRight: '8px' }}>
                    <img 
                        src={desktop.iconImage.startsWith('http') ? desktop.iconImage : `${API_BASE_URL}${desktop.iconImage}`}
                        alt={desktop.name}
                        style={{ width: '32px', height: '32px' }}
                        onError={(e) => {
                            // Hide the broken image and show default icon
                            e.target.style.display = 'none';
                            const defaultIcon = e.target.parentNode.querySelector('.default-icon');
                            if (defaultIcon) {
                                defaultIcon.style.display = 'inline-block';
                            }
                        }}
                    />
                    <span className="default-icon" style={{ display: 'none', fontSize: '32px', color: '#6c757d' }}>
                        <FaIcons.FaDesktop />
                    </span>
                </div>
            );
        }
        
        // Try to get the icon from react-icons/fa
        if (desktop.iconCode) {
            const iconName = `Fa${desktop.iconCode}`;
            const IconComponent = FaIcons[iconName];
            if (IconComponent) {
                return <IconComponent style={{ fontSize: '32px', marginRight: '8px' }} />;
            }
        }
        
        // Fallback to default desktop icon
        return <FaIcons.FaDesktop style={{ fontSize: '32px', marginRight: '8px' }} />;
    };

    // Helper function for panel icon
    const getPanelIcon = (panel) => {
        if (panel.iconImage) {
            return (
                <div style={{ display: 'inline-block', marginRight: 8 }}>
                    <img 
                        src={panel.iconImage.startsWith('http') ? panel.iconImage : `${API_BASE_URL}${panel.iconImage}`}
                        alt={panel.name}
                        style={{ width: 20, height: 20 }}
                        onError={(e) => {
                            // Hide the broken image and show default icon
                            e.target.style.display = 'none';
                            const defaultIcon = e.target.parentNode.querySelector('.default-icon');
                            if (defaultIcon) {
                                defaultIcon.style.display = 'inline-block';
                            }
                        }}
                    />
                    <span className="default-icon" style={{ display: 'none', fontSize: '1.2rem', color: '#6c757d' }}>
                        <FaTh />
                    </span>
                </div>
            );
        }
        if (panel.iconCode) {
            const iconName = `Fa${panel.iconCode}`;
            const IconComponent = FaIcons[iconName];
            if (IconComponent) {
                return <IconComponent style={{ fontSize: '1.2rem', marginRight: 8 }} />;
            }
        }
        return <FaTh style={{ fontSize: '1.2rem', marginRight: 8 }} />;
    };

    // Add this function to load landing items
    const loadLandingItems = async (desktopId) => {
        try {
            const response = await api.get(`/Desktop/${desktopId}/landing-items`);
            console.log('Landing items response:', response.data);
            
            // Backend now returns tree structure directly
            const treeItems = response.data;
            
            // Collect all category IDs for default expansion
            const categoryIds = new Set();
            const collectCategoryIds = (items) => {
                items.forEach(item => {
                    if (item.isParent) {
                        categoryIds.add(item.id);
                    }
                    if (item.children && item.children.length > 0) {
                        collectCategoryIds(item.children);
                    }
                });
            };
            collectCategoryIds(treeItems);
            
            // Set all categories as expanded by default
            setExpandedCategories(categoryIds);
            setLandingItems(treeItems);
        } catch (error) {
            console.error('Error loading landing items:', error);
            toast.error('Failed to load landing items');
        }
    };

    // Add handler for managing landing items
    const handleManageLandingItems = async (desktop) => {
        if (desktop.desktopType !== 'Landing Page') {
            toast.error('This desktop is not a Landing Page type');
            return;
        }
        setSelectedDesktop(desktop);
        setSelectedTreeNode(null); // Reset selection when opening modal
        setSelectedItem(null); // Reset item selection when opening modal
        await loadLandingItems(desktop.id);
        setShowLandingItemsModal(true);
    };

    // Add handler for editing landing items
    const handleEditLandingItem = (item) => {
        setSelectedLandingItem(item);
        setLandingItemFormData({
            name: item.name,
            description: item.description || '',
            iconCode: item.iconCode || '',
            iconImage: item.iconImage || '',
            pageCode: item.pageCode || '',
            url: item.url || '',
            linkType: item.linkType || '',  // Ensure we have a value, even if empty
            order: item.order,
            selectedFile: null,
            parentId: item.parentId || null,
            isParent: item.isParent || false
        });
        setShowLandingItemEditModal(true);
    };

    // Add handler for adding new landing items
    const handleAddLandingItem = () => {
        setSelectedLandingItem(null);
        setLandingItemFormData({
            name: '',
            description: '',
            iconCode: '',
            iconImage: '',
            pageCode: '',
            url: '',
            linkType: 'Internal',
            order: landingItems.length,
            selectedFile: null,
            parentId: null,
            isParent: false
        });
        setShowLandingItemEditModal(true);
    };

    // Add handler for adding categories (folders)
    const handleAddCategory = () => {
        setSelectedLandingItem(null);
        setLandingItemFormData({
            name: '',
            description: '',
            iconCode: '',
            iconImage: '',
            pageCode: '',
            url: '',
            linkType: null, // null for categories
            order: landingItems.length,
            selectedFile: null,
            parentId: selectedTreeNode?.id || null,
            isParent: true
        });
        setShowAddCategoryModal(true);
    };

    // Add handler for adding items within a selected category
    const handleAddItemInCategory = (parentItem) => {
        setSelectedLandingItem(null);
        setLandingItemFormData({
            name: '',
            description: '',
            iconCode: '',
            iconImage: '',
            pageCode: '',
            url: '',
            linkType: 'Internal',
            order: 0,
            selectedFile: null,
            parentId: parentItem.id,
            isParent: false
        });
        setShowAddItemModal(true);
    };

    // Add handler for adding subcategories
    const handleAddSubcategory = (parentItem) => {
        setSelectedLandingItem(null);
        setLandingItemFormData({
            name: '',
            description: '',
            iconCode: '',
            iconImage: '',
            pageCode: '',
            url: '',
            linkType: null, // null for categories
            order: 0,
            selectedFile: null,
            parentId: parentItem.id,
            isParent: true
        });
        setShowAddCategoryModal(true);
    };

    // Update handleSaveLandingItem
    const handleSaveLandingItem = async () => {
        try {
            // For categories (isParent = true), linkType should be null
            // For items (isParent = false), linkType is required
            if (!landingItemFormData.isParent) {
                const validLinkTypes = ['Internal', 'ExternalLink', 'File'];
                if (!landingItemFormData.linkType || !validLinkTypes.includes(landingItemFormData.linkType)) {
                    toast.error('Please select a valid link type');
                    return;
                }
            }
            
            let itemData = {
                name: landingItemFormData.name,
                iconCode: landingItemFormData.iconCode,
                pageCode: landingItemFormData.pageCode,
                url: (landingItemFormData.linkType === 'External' || landingItemFormData.linkType === 'ExternalLink') ? landingItemFormData.url : null,
                linkType: landingItemFormData.isParent ? null : landingItemFormData.linkType, // null for categories
                order: landingItemFormData.order,
                desktopId: selectedDesktop.id,
                isParent: landingItemFormData.isParent
            };

            // Only include parentId if it's not null
            if (landingItemFormData.parentId !== null && landingItemFormData.parentId !== undefined) {
                itemData.parentId = landingItemFormData.parentId;
            }

            // Only include description if it's not empty
            if (landingItemFormData.description && landingItemFormData.description.trim()) {
                itemData.description = landingItemFormData.description.trim();
            }

            console.log('Sending item data:', itemData);

            const iconFile = landingItemFormData.iconImage instanceof File ? landingItemFormData.iconImage : null;
            const uploadFile = landingItemFormData.selectedFile || null;

            if (selectedLandingItem) {
                await desktopService.updateLandingItem(selectedLandingItem.id, itemData, iconFile, uploadFile);
                toast.success('Landing item updated successfully');
            } else {
                await desktopService.createLandingItem(itemData, iconFile, uploadFile);
                toast.success('Landing item created successfully');
            }
            
            setShowLandingItemEditModal(false);
            loadLandingItems(selectedDesktop.id);
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Failed to save landing item');
        }
    };

    // Add handler for deleting landing items
    const handleDeleteLandingItem = async (id) => {
        if (window.confirm('Are you sure you want to delete this item?')) {
            try {
                await api.delete(`/Desktop/landing-items/${id}`);
                toast.success('Landing item deleted successfully');
                loadLandingItems(selectedDesktop.id);
            } catch (error) {
                toast.error('Failed to delete landing item');
            }
        }
    };

    const handleDeleteDesktopIcon = async () => {
        try {
            await desktopService.deleteDesktopIcon(selectedDesktop.id);
            setFormData({ ...formData, iconImage: '' });
            toast.success('Icon deleted successfully');
        } catch (error) {
            toast.error('Failed to delete icon');
        }
    };

    const handleDeletePanelIcon = async () => {
        try {
            await desktopService.deletePanelIcon(selectedPanel.id);
            setPanelFormData({ ...panelFormData, iconImage: '' });
            toast.success('Icon deleted successfully');
        } catch (error) {
            toast.error('Failed to delete icon');
        }
    };

    // Add handler for toggling category expansion
    const toggleCategoryExpansion = (categoryId) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(categoryId)) {
                newSet.delete(categoryId);
            } else {
                newSet.add(categoryId);
            }
            return newSet;
        });
    };

    const handleDeleteLandingItemIcon = async () => {
        try {
            await desktopService.deleteLandingItemIcon(selectedLandingItem.id);
            setLandingItemFormData({ ...landingItemFormData, iconImage: '' });
            toast.success('Icon deleted successfully');
        } catch (error) {
            toast.error('Failed to delete icon');
        }
    };

    // Add handler for selecting items
    const handleItemSelect = (item) => {
        if (selectedItem && selectedItem.id === item.id) {
            // Deselect if clicking the same item
            setSelectedItem(null);
        } else {
            // Select the new item
            setSelectedItem(item);
        }
    };

    // Add handler for resetting selection
    const resetSelection = () => {
        setSelectedItem(null);
    };

    // Landing Item Edit Modal Form
    const renderLandingItemForm = () => (
        <Form>
            <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                    type="text"
                    value={landingItemFormData.name}
                    onChange={(e) => setLandingItemFormData({ ...landingItemFormData, name: e.target.value })}
                    required
                />
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                    as="textarea"
                    value={landingItemFormData.description}
                    onChange={(e) => setLandingItemFormData({ ...landingItemFormData, description: e.target.value })}
                />
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>Type</Form.Label>
                <Form.Select
                    value={landingItemFormData.isParent ? 'category' : 'item'}
                    onChange={(e) => {
                        const isCategory = e.target.value === 'category';
                        setLandingItemFormData({ 
                            ...landingItemFormData, 
                            isParent: isCategory,
                            linkType: isCategory ? null : 'Internal',
                            // Clear URL and file when changing type
                            url: '',
                            selectedFile: null,
                            pageCode: ''
                        });
                    }}
                    required
                >
                    <option value="category">Category (Folder)</option>
                    <option value="item">Item (Link/File)</option>
                </Form.Select>
            </Form.Group>
            {!landingItemFormData.isParent && (
                <>
                    <Form.Group className="mb-3">
                        <Form.Label>Link Type</Form.Label>
                        <Form.Select
                            value={landingItemFormData.linkType || ''}
                            onChange={(e) => setLandingItemFormData({ 
                                ...landingItemFormData, 
                                linkType: e.target.value,
                                // Clear URL and file when changing type
                                url: '',
                                selectedFile: null
                            })}
                            required
                            isInvalid={!landingItemFormData.linkType}
                        >
                            <option value="">Select link type</option>
                            <option value="Internal">Internal Link</option>
                            <option value="ExternalLink">External Link</option>
                            <option value="File">File</option>
                        </Form.Select>
                    </Form.Group>
                    {landingItemFormData.linkType === 'Internal' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Page Code</Form.Label>
                            <Form.Control
                                type="text"
                                value={landingItemFormData.pageCode}
                                onChange={(e) => setLandingItemFormData({ ...landingItemFormData, pageCode: e.target.value })}
                                required
                            />
                        </Form.Group>
                    )}
                    {landingItemFormData.linkType === 'ExternalLink' && (
                        <Form.Group className="mb-3">
                            <Form.Label>URL</Form.Label>
                            <Form.Control
                                type="text"
                                value={landingItemFormData.url}
                                onChange={(e) => setLandingItemFormData({ ...landingItemFormData, url: e.target.value })}
                                required
                            />
                        </Form.Group>
                    )}
                    {landingItemFormData.linkType === 'File' && (
                        <Form.Group className="mb-3">
                            <Form.Label>File</Form.Label>
                            <Form.Control
                                type="file"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        setLandingItemFormData({
                                            ...landingItemFormData,
                                            selectedFile: file
                                        });
                                    }
                                }}
                            />
                            {selectedLandingItem?.url && (
                                <Form.Text className="text-muted">
                                    Current file: {selectedLandingItem.url}
                                </Form.Text>
                            )}
                        </Form.Group>
                    )}
                </>
            )}
            <Form.Group className="mb-3">
                <Form.Label>Icon</Form.Label>
                <IconPicker
                    selectedIcon={landingItemFormData.iconCode}
                    onIconSelect={(code) => setLandingItemFormData({ ...landingItemFormData, iconCode: code })}
                />
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>Icon Image</Form.Label>
                <div className="d-flex align-items-center gap-3">
                    <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => setLandingItemFormData({ ...landingItemFormData, iconImage: e.target.files[0] })}
                    />
                    {landingItemFormData.iconImage && (
                        <div className="position-relative">
                            <img 
                                src={landingItemFormData.iconImage instanceof File 
                                    ? URL.createObjectURL(landingItemFormData.iconImage)
                                    : landingItemFormData.iconImage.startsWith('http') 
                                        ? landingItemFormData.iconImage 
                                        : `${API_BASE_URL}${landingItemFormData.iconImage}`}
                                alt="Landing item icon"
                                style={{ width: '32px', height: '32px' }}
                            />
                            <button
                                type="button"
                                onClick={handleDeleteLandingItemIcon}
                                className="position-absolute top-0 end-0 bg-danger text-white rounded-circle border-0"
                                style={{ 
                                    width: '16px',
                                    height: '16px',
                                    fontSize: '10px',
                                    lineHeight: '1',
                                    transform: 'translate(50%, -50%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 0
                                }}
                            >
                                ×
                            </button>
                        </div>
                    )}
                </div>
            </Form.Group>
        </Form>
    );

    const renderLandingItemInfo = (item) => {
        switch (item.linkType) {
            case 'Internal':
                return `Internal Link - Page: ${item.pageCode}`;
            case 'ExternalLink':
                return `External Link - URL: ${item.url}`;
            case 'File':
                return `File - ${item.url ? item.url.split('/').pop() : 'No file uploaded'}`;
            default:
                return item.isParent ? 'Category (Folder)' : 'Unknown Type';
        }
    };

    // Recursive tree view component for landing items
    const renderLandingItemTree = (items, level = 0) => {
        return items.map((item) => (
            <div key={item.id} className="landing-item-tree-node" style={{ marginLeft: `${level * 30}px` }}>
                <div className="d-flex align-items-center mb-2">
                    {/* Expand/Collapse button for categories with children - outside the container */}
                    {item.isParent && item.children && item.children.length > 0 && (
                        <button 
                            className="toggle-btn me-2"
                            onClick={() => toggleCategoryExpansion(item.id)}
                        >
                            {expandedCategories.has(item.id) ? <FaIcons.FaMinus /> : <FaIcons.FaPlus />}
                        </button>
                    )}
                    {/* Main item container */}
                    <div 
                        className={`task-item mb-1 p-2 rounded landing-item-container ${selectedItem && selectedItem.id === item.id ? 'selected' : ''}`}
                        onClick={() => handleItemSelect(item)}
                        style={{ 
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            border: '1px solid rgba(0,0,0,0.05)',
                            width: '100%'
                        }}
                        onMouseEnter={(e) => {
                            if (!selectedItem || selectedItem.id !== item.id) {
                                e.currentTarget.classList.add('landing-item-hover');
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!selectedItem || selectedItem.id !== item.id) {
                                e.currentTarget.classList.remove('landing-item-hover');
                            }
                        }}
                    >
                        <div className="mb-1">
                            <div className="d-flex justify-content-between align-items-center gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    {/* Landing Item Icon */}
                                    <div style={{ display: 'inline-block', marginRight: '8px' }}>
                                        {item.iconImage ? (
                                            <img 
                                                src={item.iconImage.startsWith('http') ? item.iconImage : `${API_BASE_URL}${item.iconImage}`}
                                                alt={item.name}
                                                style={{ width: '24px', height: '24px' }}
                                                onError={(e) => {
                                                    // Hide the broken image and show default icon
                                                    e.target.style.display = 'none';
                                                    const defaultIcon = e.target.parentNode.querySelector('.default-icon');
                                                    if (defaultIcon) {
                                                        defaultIcon.style.display = 'inline-block';
                                                    }
                                                }}
                                            />
                                        ) : item.iconCode ? (
                                            (() => {
                                                const iconName = `Fa${item.iconCode}`;
                                                const IconComponent = FaIcons[iconName];
                                                return IconComponent ? 
                                                    <IconComponent style={{ fontSize: '24px', color: 'inherit' }} /> : 
                                                    <FaTh style={{ fontSize: '24px', color: 'inherit' }} />;
                                            })()
                                        ) : (
                                            <FaTh style={{ fontSize: '24px', color: 'inherit' }} />
                                        )}
                                        <span className="default-icon" style={{ display: 'none', fontSize: '24px', color: 'inherit' }}>
                                            <FaTh />
                                        </span>
                                    </div>
                                    <h6 className="mb-0 me-1 small">{item.name}</h6>
                                    {item.isParent && (
                                        <span className="badge bg-info ms-2">Category</span>
                                    )}
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => handleEditLandingItem(item)}
                                    >
                                        <BsPencil />
                                    </Button>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleDeleteLandingItem(item.id)}
                                    >
                                        <BsTrash />
                                    </Button>
                                </div>
                            </div>
                            
                            {item.description && (
                                <div className="text-muted" style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                                    {item.description}
                                </div>
                            )}
                            
                            <div className="text-muted" style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                                {renderLandingItemInfo(item)}
                            </div>
                        </div>
                    </div>
                </div>
                {/* Show children only if category is expanded */}
                {item.children && item.children.length > 0 && expandedCategories.has(item.id) && (
                    <div className="landing-item-children">
                        {renderLandingItemTree(item.children, level + 1)}
                    </div>
                )}
            </div>
        ));
    };

    // Landing Items Modal
    const renderLandingItemsModal = () => (
        <Modal show={showLandingItemsModal} onHide={() => setShowLandingItemsModal(false)} size={isSmallPanel ? "sm" : "lg"}>
            <Modal.Header closeButton>
                <Modal.Title>Landing Page Items - {selectedDesktop?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h5>
                            Items
                            {selectedItem && (
                                <span className="text-muted small ms-2 rounded px-2 py-1">
                                    selected: {selectedItem.name}
                                    <span 
                                        className="ms-2" 
                                        style={{ cursor: 'pointer', color: '#dc3545' }}
                                        onClick={resetSelection}
                                        title="Deselect"
                                    >
                                        <FaTimes />
                                    </span>
                                </span>
                            )}
                        </h5>
                    </div>
                    <div className="d-flex gap-2">
                        {(!selectedItem || selectedItem.isParent) && (
                            <Button 
                                variant="success" 
                                size="sm"
                                onClick={() => {
                                    // Reset selected item to ensure we're not in edit mode
                                    setSelectedLandingItem(null);
                                    setLandingItemFormData({
                                        name: '',
                                        description: '',
                                        iconCode: '',
                                        iconImage: '',
                                        pageCode: '',
                                        url: '',
                                        linkType: null,
                                        order: landingItems.length,
                                        selectedFile: null,
                                        parentId: selectedItem?.isParent ? selectedItem?.id : null,
                                        isParent: true
                                    });
                                    setShowAddCategoryModal(true);
                                }}
                            >
                                <BsPlusLg /> Add Category
                            </Button>
                        )}
                        <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => {
                                // Reset selected item to ensure we're not in edit mode
                                setSelectedLandingItem(null);
                                setLandingItemFormData({
                                    name: '',
                                    description: '',
                                    iconCode: '',
                                    iconImage: '',
                                    pageCode: '',
                                    url: '',
                                    linkType: 'Internal',
                                    order: landingItems.length,
                                    selectedFile: null,
                                    parentId: selectedItem?.isParent ? selectedItem?.id : null,
                                    isParent: false
                                });
                                setShowAddItemModal(true);
                            }}
                        >
                            <BsPlusLg /> Add Item
                        </Button>
                    </div>
                </div>
                
                <div className="list-group">
                    {landingItems.length > 0 ? (
                        renderLandingItemTree(landingItems)
                    ) : (
                        <div className="text-muted text-center py-4">
                            <p>No landing items found.</p>
                            <p>Click "Add Category" to create a folder or "Add Item" to create a link/file.</p>
                        </div>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );

    // Modify the Desktop Modal Form section to include the page dropdown
    const renderDesktopForm = () => (
                    <Form onSubmit={handleDesktopSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Desktop Type</Form.Label>
                            <Form.Select
                                value={formData.desktopType}
                                onChange={(e) => handleDesktopTypeChange(e.target.value)}
                                required
                            >
                                <option value="">Select a type</option>
                                <option value="Dashboard">Dashboard</option>
                                <option value="Landing Page">Landing Page</option>
                                <option value="Internal Link">Internal Link</option>
                                <option value="External Link">External Link</option>
                                <option value="Other">Other</option>
                            </Form.Select>
                        </Form.Group>
                        {formData.desktopType === 'External Link' && (
                            <Form.Group className="mb-3">
                                <Form.Label>URL</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.url}
                                    onChange={(e) => {
                                        let url = e.target.value.trim();
                                        setFormData({ ...formData, url });
                                    }}
                                    onBlur={(e) => {
                                        let url = e.target.value.trim();
                                        if (url && !url.match(/^https?:\/\//i)) {
                                            url = `https://${url}`;
                                            setFormData({ ...formData, url });
                                        }
                                    }}
                                    placeholder="Enter URL (e.g., https://example.com)"
                                    required
                                />
                                <Form.Text className="text-muted">
                                    Enter a complete URL including https:// or http://. If not specified, https:// will be added automatically.
                                </Form.Text>
                            </Form.Group>
                        )}
                        {formData.desktopType === 'Internal Link' && (
                            <Form.Group className="mb-3">
                    <Form.Label>Page</Form.Label>
                    <Form.Select
                                    value={formData.pageCode}
                                    onChange={(e) => setFormData({ ...formData, pageCode: e.target.value })}
                                    required
                        disabled={loadingPages}
                    >
                        <option value="">Select a page</option>
                        {pages.map(page => (
                            <option key={page.id} value={page.code}>
                                {page.name} ({page.code})
                            </option>
                        ))}
                    </Form.Select>
                    {loadingPages && (
                        <Form.Text className="text-muted">
                            Loading pages...
                        </Form.Text>
                    )}
                            </Form.Group>
                        )}
                        {formData.desktopType === 'Other' && (
                            <Form.Group className="mb-3">
                                <Form.Label>Custom Type</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.customType}
                                    onChange={(e) => setFormData({ ...formData, customType: e.target.value })}
                                    placeholder="Enter custom type"
                                    required
                                />
                            </Form.Group>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Icon Code</Form.Label>
                            <IconPicker
                                selectedIcon={formData.iconCode}
                                onIconSelect={(code) => setFormData({ ...formData, iconCode: code })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Icon Image</Form.Label>
                            <div className="d-flex align-items-center gap-3">
                                <Form.Control
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => setFormData({ ...formData, iconImage: e.target.files[0] })}
                                />
                                {formData.iconImage && (
                                    <div className="position-relative">
                                        <img 
                                            src={formData.iconImage instanceof File 
                                                ? URL.createObjectURL(formData.iconImage)
                                                : formData.iconImage.startsWith('http') 
                                                    ? formData.iconImage 
                                                    : `${API_BASE_URL}${formData.iconImage}`}
                                            alt="Desktop icon"
                                            style={{ width: '32px', height: '32px' }}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleDeleteDesktopIcon}
                                            className="position-absolute top-0 end-0 bg-danger text-white rounded-circle border-0"
                                            style={{ 
                                                width: '16px',
                                                height: '16px',
                                                fontSize: '10px',
                                                lineHeight: '1',
                                                transform: 'translate(50%, -50%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: 0
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Is Home"
                                checked={formData.isHome}
                                onChange={(e) => setFormData({ ...formData, isHome: e.target.checked })}
                            />
                        </Form.Group>
                        <Button variant="primary" type="submit">
                            Save
                        </Button>
                    </Form>
    );

    if (loading) {
        return <Spinner />;
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Container fluid className="p-4 desktop-management-panel">
                <Row>
                    {/* Organizational Units Section */}
                    <Col md={isSmallPanel ? 12 : 4} className={`${isSmallPanel ? 'mb-3' : ''} desktop-column`}>
                        <Card>
                            <Card.Header>
                                <h4>{isSmallPanel ? 'Units' : 'Organizational Units'}</h4>
                            </Card.Header>
                            <Card.Body style={{ maxHeight: isSmallPanel ? '400px' : '600px', overflowY: 'auto' }}>
                                <div className="search-container mb-3">
                                    <InputGroup>
                                        <FormControl
                                        placeholder="Search units or users..."
                                        value={unitSearch}
                                        onChange={(e) => setUnitSearch(e.target.value)}
                                            style={textStyle}
                                    />
                                        <InputGroup.Text style={textStyle}>
                                            <FaSearch />
                                        </InputGroup.Text>
                                    </InputGroup>
                                </div>
                                <Droppable droppableId="units-container" isDropDisabled={true}>
                                    {(provided) => (
                                        <div 
                                            className="tree-container"
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            style={{ maxHeight: isSmallPanel ? '300px' : '600px', overflowY: 'auto' }}
                                        >
                                            {orgUnits && orgUnits.length > 0 ? (
                                                renderUnitTree(orgUnits)
                                            ) : (
                                                <div className="text-muted">No organizational units found</div>
                                            )}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Desktops Section */}
                    <Col md={isSmallPanel ? 12 : 8} className="desktop-column">
                        <Card>
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h4>Desktops</h4>
                                <Button 
                                    variant="primary" 
                                    size={isSmallPanel ? "sm" : undefined}
                                    style={{ color: 'white' }}
                                    onClick={() => {
                                    setSelectedDesktop(null);
                                    setFormData({
                                        name: '',
                                        description: '',
                                        iconCode: '',
                                        iconImage: '',
                                        pageCode: '',
                                        url: '',
                                        order: desktops.length,
                                        isHome: false,
                                        desktopType: '',
                                        customType: ''
                                    });
                                    setShowDesktopModal(true);
                                    }}
                                >
                                    <BsPlusLg style={{ color: 'white' }} /> Add Desktop
                                </Button>
                            </Card.Header>
                                                        <Card.Body style={{ maxHeight: isSmallPanel ? '400px' : '600px', overflowY: 'auto' }}>
                                <div className="list-group">
                                    {desktops.map((desktop, index) => (
                                        <Droppable key={`desktop-${desktop.id}`} droppableId={`desktop-${desktop.id}`}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    className={`list-group-item mb-2 desktop-drop-zone ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                                                    style={{ minHeight: '80px' }}
                                                >
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div className="d-flex align-items-start flex-grow-1">
                                                            {renderIcon(desktop)}
                                                            <div className="flex-grow-1">
                                                                <h5 className="mb-1" style={textStyle}>{desktop.name}</h5>
                                                                    {!isSmallPanel && (
                                                                <p className="mb-1 text-muted">{desktop.description}</p>
                                                                    )}
                                                                <small className="text-muted">Type: {desktop.desktopType}</small>
                                                                {/* --- Assigned Access Section --- */}
                                                                    {desktopAccessInfo[desktop.id] && (
                                                                    <div className="mt-2">
                                                                        <div className="d-flex flex-wrap gap-1 align-items-center">
                                                                            {/* Users */}
                                                                            {desktopAccessInfo[desktop.id].userAccesses && desktopAccessInfo[desktop.id].userAccesses.map(user => (
                                                                                <span key={user.accessControlId} className="badge bg-light text-dark d-flex align-items-center gap-1 px-2 py-1">
                                                                                    {user.imageUrl ? (
                                                                                        <ProfileImage 
                                                                                            imageUrl={user.imageUrl}
                                                                                            width={15}
                                                                                            height={15}
                                                                                        />
                                                                                    ) : (
                                                                                        <FaUser style={{ fontSize: '0.8rem' }} />
                                                                                    )}
                                                                                    <span style={{ fontSize: '0.8rem' }}>{user.fullName || user.username || 'Unknown User'}</span>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn-close btn-close-sm ms-1"
                                                                                        style={{ fontSize: '0.6rem' }}
                                                                                        onClick={() => handleRemoveAccess(user.accessControlId, desktop.id)}
                                                                                        title="Remove access"
                                                                                    />
                                                                                </span>
                                                                            ))}
                                                                            {/* Units */}
                                                                            {desktopAccessInfo[desktop.id].unitAccesses && desktopAccessInfo[desktop.id].unitAccesses.map(unit => (
                                                                                <span key={unit.accessControlId} className="badge d-flex align-items-center gap-1 px-2 py-1" style={{ backgroundColor: unit.unitTypeColor || '#e3f2fd' }}>
                                                                                    {getIconComponent(unit.unitTypeIcon) || <FaBuilding style={{ fontSize: '0.8rem' }} />}
                                                                                    <span style={{ fontSize: '0.8rem' }}>{unit.unitName}</span>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn-close btn-close-sm ms-1"
                                                                                        style={{ fontSize: '0.6rem' }}
                                                                                        onClick={() => handleRemoveAccess(unit.accessControlId, desktop.id)}
                                                                                        title="Remove access"
                                                                                    />
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {snapshot.isDraggingOver && (
                                                                    <div className="mt-2">
                                                                        <small className="text-primary">
                                                                            Drop here to assign access
                                                                        </small>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="d-flex align-items-center flex-shrink-0">
                                                            {desktop.isHome && (
                                                                <span className="badge bg-success me-2">Home</span>
                                                            )}
                                                            <div className="d-flex align-items-center">
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size={isSmallPanel ? "sm" : "sm"}
                                                                    className={`me-${isSmallPanel ? '1' : '2'}`}
                                                                    onClick={() => handleEdit(desktop)}
                                                                    title="Edit"
                                                                >
                                                                    <BsPencil />
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size={isSmallPanel ? "sm" : "sm"}
                                                                    className={`me-${isSmallPanel ? '1' : '2'}`}
                                                                    onClick={() => handleDelete(desktop.id)}
                                                                    title="Delete"
                                                                >
                                                                    <BsTrash />
                                                                </Button>
                                                                {desktop.desktopType === 'Dashboard' && (
                                                                    <Button
                                                                        variant="outline-info"
                                                                        size={isSmallPanel ? "sm" : "sm"}
                                                                        className={`me-${isSmallPanel ? '1' : '2'}`}
                                                                        onClick={() => handleManagePanels(desktop)}
                                                                        title="Manage Panels"
                                                                    >
                                                                        <BsGrid3X3Gap />
                                                                    </Button>
                                                                )}
                                                                {desktop.desktopType === 'Landing Page' && (
                                                                    <Button
                                                                        variant="outline-info"
                                                                        size={isSmallPanel ? "sm" : "sm"}
                                                                        className="manage-items-btn"
                                                                        onClick={() => handleManageLandingItems(desktop)}
                                                                        title="Manage Items"
                                                                        style={{
                                                                            borderColor: '#17a2b8',
                                                                            color: '#17a2b8'
                                                                        }}
                                                                    >
                                                                        <BsGridFill style={{ color: '#17a2b8' }} />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                            {/* Reorder Buttons */}
                                                            {!isSmallPanel && (
                                                            <div className="reorder-buttons ms-2">
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    size="sm"
                                                                    className="reorder-btn"
                                                                    disabled={index === 0}
                                                                    onClick={() => handleMoveDesktop(desktop.id, 'up')}
                                                                    title="Move up"
                                                                >
                                                                    <BsChevronUp />
                                                                </Button>
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    size="sm"
                                                                    className="reorder-btn"
                                                                    disabled={index === desktops.length - 1}
                                                                    onClick={() => handleMoveDesktop(desktop.id, 'down')}
                                                                    title="Move down"
                                                                >
                                                                    <BsChevronDown />
                                                                </Button>
                                                            </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

            {/* Desktop Modal */}
            <Modal show={showDesktopModal} onHide={() => setShowDesktopModal(false)} size={isSmallPanel ? "sm" : "lg"}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedDesktop ? 'Edit Desktop' : 'Add Desktop'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {renderDesktopForm()}
                </Modal.Body>
            </Modal>

            {/* Panel Management Modal */}
            <Modal show={showPanelModal} onHide={() => setShowPanelModal(false)} size={isSmallPanel ? "sm" : "lg"}>
                <Modal.Header closeButton>
                    <Modal.Title>Manage Panels - {selectedDesktop?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5>Panels</h5>
                        <Button 
                            variant="primary" 
                            size={isSmallPanel ? "sm" : "sm"}
                            onClick={() => {
                                setSelectedPanel(null);
                                setPanelFormData({
                                    name: '',
                                    description: '',
                                    iconCode: '',
                                    iconImage: '',
                                    code: '',
                                    position: 'left',
                                    order: panels.length,
                                    size: 'medium',
                                    desktopId: selectedDesktop.id
                                });
                                setShowPanelEditModal(true);
                            }}
                        >
                            <BsPlusLg /> Add Panel
                        </Button>
                    </div>
                    <div className="list-group">
                        {panels.map((panel) => (
                            <div key={panel.id} className="list-group-item mb-2 d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="d-flex align-items-center mb-1">
                                        {getPanelIcon(panel)}
                                        <span>{panel.name}</span>
                                    </div>
                                    <div className="text-muted" style={{ fontSize: '0.95em' }}>
                                        Type: {panel.code} | Position: {panel.position} | Size: {panel.size}
                                    </div>
                                    {panel.description && (
                                        <div className="mb-1 text-muted" style={{ fontSize: '0.9em' }}>{panel.description}</div>
                                    )}
                                </div>
                                <div>
                                    <div className="d-flex gap-1">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => handleEditPanel(panel)}
                                        >
                                            <BsPencil />
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDeletePanel(panel.id)}
                                        >
                                            <BsTrash />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
            </Modal>

            {/* Panel Edit Modal */}
            <Modal show={showPanelEditModal} onHide={() => setShowPanelEditModal(false)} size={isSmallPanel ? "sm" : undefined}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedPanel ? 'Edit Panel' : 'Add Panel'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={panelFormData.name}
                                onChange={(e) => setPanelFormData({ ...panelFormData, name: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={panelFormData.description}
                                onChange={(e) => setPanelFormData({ ...panelFormData, description: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Type</Form.Label>
                            <Form.Select
                                value={panelFormData.code || ''}
                                onChange={(e) => setPanelFormData({ ...panelFormData, code: e.target.value })}
                                required
                            >
                                <option value="">Select a type</option>
                                {codeOptions.map(option => (
                                    <option 
                                        key={option} 
                                        value={option}
                                        disabled={disabledCodes[option]}
                                    >
                                        {option}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Position</Form.Label>
                            <Form.Select
                                value={panelFormData.position}
                                onChange={(e) => setPanelFormData({ ...panelFormData, position: e.target.value })}
                            >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Size</Form.Label>
                            <Form.Select
                                value={panelFormData.size}
                                onChange={(e) => setPanelFormData({ ...panelFormData, size: e.target.value })}
                            >
                                <option value="small">Small</option>
                                <option value="medium">Medium</option>
                                <option value="large">Large</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Icon</Form.Label>
                            <IconPicker
                                selectedIcon={panelFormData.iconCode}
                                onIconSelect={(code) => setPanelFormData({ ...panelFormData, iconCode: code })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Icon Image</Form.Label>
                            <div className="d-flex align-items-center gap-3">
                                <Form.Control
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => setPanelFormData({ ...panelFormData, iconImage: e.target.files[0] })}
                                />
                                {panelFormData.iconImage && (
                                    <div className="position-relative">
                                        <img 
                                            src={panelFormData.iconImage instanceof File 
                                                ? URL.createObjectURL(panelFormData.iconImage)
                                                : panelFormData.iconImage.startsWith('http') 
                                                    ? panelFormData.iconImage 
                                                    : `${API_BASE_URL}${panelFormData.iconImage}`}
                                            alt="Panel icon"
                                            style={{ width: '32px', height: '32px' }}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleDeletePanelIcon}
                                            className="position-absolute top-0 end-0 bg-danger text-white rounded-circle border-0"
                                            style={{ 
                                                width: '16px',
                                                height: '16px',
                                                fontSize: '10px',
                                                lineHeight: '1',
                                                transform: 'translate(50%, -50%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: 0
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                            </div>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPanelEditModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSavePanel}>
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Landing Items Modal */}
            {renderLandingItemsModal()}

            {/* Landing Item Edit Modal */}
            <Modal show={showLandingItemEditModal} onHide={() => setShowLandingItemEditModal(false)} size={isSmallPanel ? "sm" : undefined}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedLandingItem ? 'Edit Item' : 
                         landingItemFormData.isParent ? 'Add Category' : 'Add Item'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {renderLandingItemForm()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowLandingItemEditModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveLandingItem}>
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Category Modal */}
            <Modal show={showAddCategoryModal} onHide={() => setShowAddCategoryModal(false)} size={isSmallPanel ? "sm" : undefined}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {landingItemFormData.parentId ? 
                            `Add Subcategory to "${selectedTreeNode?.name}"` : 
                            'Add Category (Root Level)'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Category Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={landingItemFormData.name}
                                onChange={(e) => setLandingItemFormData({ ...landingItemFormData, name: e.target.value })}
                                placeholder="Enter category name"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={landingItemFormData.description}
                                onChange={(e) => setLandingItemFormData({ ...landingItemFormData, description: e.target.value })}
                                placeholder="Enter category description (optional)"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Icon</Form.Label>
                            <IconPicker
                                selectedIcon={landingItemFormData.iconCode}
                                onIconSelect={(code) => setLandingItemFormData({ ...landingItemFormData, iconCode: code })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Icon Image</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => setLandingItemFormData({ ...landingItemFormData, iconImage: e.target.files[0] })}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddCategoryModal(false)}>
                        Cancel
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={() => {
                            handleSaveLandingItem();
                            setShowAddCategoryModal(false);
                        }}
                        disabled={!landingItemFormData.name.trim()}
                    >
                        Create Category
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Item Modal */}
            <Modal show={showAddItemModal} onHide={() => setShowAddItemModal(false)} size={isSmallPanel ? "sm" : undefined}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {landingItemFormData.parentId ? 
                            `Add Item to "${selectedTreeNode?.name}"` : 
                            'Add Item (Root Level)'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Item Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={landingItemFormData.name}
                                onChange={(e) => setLandingItemFormData({ ...landingItemFormData, name: e.target.value })}
                                placeholder="Enter item name"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={landingItemFormData.description}
                                onChange={(e) => setLandingItemFormData({ ...landingItemFormData, description: e.target.value })}
                                placeholder="Enter item description (optional)"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Link Type</Form.Label>
                            <Form.Select
                                value={landingItemFormData.linkType || ''}
                                onChange={(e) => setLandingItemFormData({ 
                                    ...landingItemFormData, 
                                    linkType: e.target.value,
                                    url: '',
                                    selectedFile: null,
                                    pageCode: ''
                                })}
                                required
                            >
                                <option value="">Select link type</option>
                                <option value="Internal">Internal Link</option>
                                <option value="ExternalLink">External Link</option>
                                <option value="File">File</option>
                            </Form.Select>
                        </Form.Group>
                        {landingItemFormData.linkType === 'Internal' && (
                            <Form.Group className="mb-3">
                                <Form.Label>Page Code</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={landingItemFormData.pageCode}
                                    onChange={(e) => setLandingItemFormData({ ...landingItemFormData, pageCode: e.target.value })}
                                    placeholder="Enter page code"
                                    required
                                />
                            </Form.Group>
                        )}
                        {landingItemFormData.linkType === 'ExternalLink' && (
                            <Form.Group className="mb-3">
                                <Form.Label>URL</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={landingItemFormData.url}
                                    onChange={(e) => setLandingItemFormData({ ...landingItemFormData, url: e.target.value })}
                                    placeholder="Enter URL"
                                    required
                                />
                            </Form.Group>
                        )}
                        {landingItemFormData.linkType === 'File' && (
                            <Form.Group className="mb-3">
                                <Form.Label>File</Form.Label>
                                <Form.Control
                                    type="file"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setLandingItemFormData({
                                                ...landingItemFormData,
                                                selectedFile: file
                                            });
                                        }
                                    }}
                                />
                            </Form.Group>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Icon</Form.Label>
                            <IconPicker
                                selectedIcon={landingItemFormData.iconCode}
                                onIconSelect={(code) => setLandingItemFormData({ ...landingItemFormData, iconCode: code })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Icon Image</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => setLandingItemFormData({ ...landingItemFormData, iconImage: e.target.files[0] })}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddItemModal(false)}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={() => {
                            handleSaveLandingItem();
                            setShowAddItemModal(false);
                        }}
                        disabled={!landingItemFormData.name.trim() || !landingItemFormData.linkType}
                    >
                        Create Item
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Access Control Modal */}
            {showAccessModal && (
                <DesktopAccessControlModal
                    isOpen={showAccessModal}
                    title={accessModalData.type === 'unit' ? "Assign Unit Access" : "Assign User Access"}
                    onClose={handleAccessModalClose}
                    footer={
                        <div className="access-modal-footer">
                            <button className="btn btn-secondary" onClick={handleAccessModalClose}>
                                Cancel
                            </button>
                            <button 
                                className="btn btn-primary" 
                                onClick={handleAssignAccess}
                            >
                                Assign Access
                            </button>
                        </div>
                    }
                >
                    <div className="access-modal-form">
                        {/* Assignment Information */}
                        <div className="modal-selection-info">
                            <h3 className="assignment-title">Access Assignment</h3>
                            <div className="assignment-details">
                                <div className="assignment-item">
                                    <span className="assignment-label">From:</span>
                                    <span className="assignment-value">
                                        {accessModalData.type === 'unit' ? (
                                            <><FaBuilding style={{ marginRight: '5px' }} /> {accessModalData.sourceName}</>
                                        ) : (
                                            <><FaUser style={{ marginRight: '5px' }} /> {accessModalData.sourceName}</>
                                        )}
                                    </span>
                                </div>
                                <div className="assignment-arrow">→</div>
                                <div className="assignment-item">
                                    <span className="assignment-label">To:</span>
                                    <span className="assignment-value">
                                        <FaFile style={{ marginRight: '5px' }} /> {accessModalData.targetName}
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        {/* Exclusions Section - Only show for units */}
                        {accessModalData.type === 'unit' && accessModalData.childrenToExclude.length > 0 && (
                            <div className="form-group exclusions-section">
                                <h4 className="exclusions-title">Exclude Children from Access</h4>
                                <div className="exclusions-explanation">
                                    <p>When you grant access to <strong>{accessModalData.sourceName}</strong>, all users and sub-units within it inherit that access.</p>
                                    <p>Use this section to select specific children that should <strong>NOT</strong> receive this access permission.</p>
                                </div>
                                
                                <div className="search-input-container">
                                    <label>Search for children to exclude:</label>
                                    <input
                                        type="text"
                                        value={exclusionSearch}
                                        onChange={(e) => handleExclusionSearch(e.target.value)}
                                        placeholder="Type to search for users or sub-units..."
                                        className="form-control"
                                    />
                                    {showExclusionSuggestions && exclusionSuggestions.length > 0 && (
                                        <div className="suggestions-container">
                                            {exclusionSuggestions.map((suggestion) => (
                                                <div
                                                    key={`${suggestion.type}-${suggestion.id}`}
                                                    className="suggestion-item"
                                                    onClick={() => addExclusion(suggestion)}
                                                >
                                                    {suggestion.type === 'user' ? (
                                                        <><FaUser style={{ marginRight: '5px' }} />
                                                        {suggestion.name}</>
                                                    ) : (
                                                        <><FaBuilding style={{ marginRight: '5px' }} />
                                                        {suggestion.name}</>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Selected Exclusions */}
                                {accessModalData.exclusions.length > 0 && (
                                    <div className="selected-exclusions">
                                        <label>Selected Exclusions:</label>
                                        <ul className="exclusions-list">
                                            {accessModalData.exclusions.map((item) => (
                                                <li key={`${item.type}-${item.id}`} className="exclusion-item">
                                                    {item.type === 'user' ? (
                                                        <><FaUser style={{ marginRight: '5px' }} />
                                                        {item.name}</>
                                                    ) : (
                                                        <><FaBuilding style={{ marginRight: '5px' }} />
                                                        {item.name}</>
                                                    )}
                                                    <button 
                                                        className="remove-exclusion-btn"
                                                        onClick={() => removeExclusion(item.id, item.type)}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </DesktopAccessControlModal>
            )}
            </Container>
        </DragDropContext>
    );
};

export default DesktopManagement; 